import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

export interface FeedbackModalProps {
  open: boolean;
  messageId: number;
  onClose: (messageId: number) => void;
  onSubmit: (messageId: number, reason: string) => void;
}

const FEEDBACK_REASONS = [
  "Recusou quando não deveria",
  "Resposta estava incorreta",
  "Verbosidade excessiva",
  "Não seguiu totalmente as instruções",
  "Alucinou informações",
];

function FeedbackModal({
  open,
  messageId,
  onClose,
  onSubmit,
}: FeedbackModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');

  const customReasonInputId = `custom-feedback-${messageId}`;

  useEffect(() => {
    if (!open) {
      setSelectedReason('');
      setCustomReason('');
    }
  }, [open]);

  const trimmedCustomReason = customReason.trim();
  const canSubmit = Boolean(selectedReason || trimmedCustomReason);
  const resolvedReason = trimmedCustomReason || selectedReason;

  if (!open) return null;

  return (
    <ModalOverlay onClick={() => onClose(messageId)}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <FeedbackModalHeader>
          <FeedbackModalTitle>
            Escolha um motivo:
          </FeedbackModalTitle>
          <FeedbackCloseButton onClick={() => onClose(messageId)}>
            <CloseIcon />
          </FeedbackCloseButton>
        </FeedbackModalHeader>
        {FEEDBACK_REASONS.map((reason, index) => (
          <FeedbackReasonButton
            key={index}
            selected={selectedReason === reason}
            onClick={() => setSelectedReason(reason)}
          >
            {reason}
          </FeedbackReasonButton>
        ))}
        <FeedbackCustomReasonLabel htmlFor={customReasonInputId}>
          Outro motivo
        </FeedbackCustomReasonLabel>
        <FeedbackCustomReasonInput
          id={customReasonInputId}
          placeholder="Descreva o motivo do feedback"
          value={customReason}
          onChange={(event) => setCustomReason(event.target.value)}
        />
        <FeedbackSubmitButton
          disabled={!canSubmit}
          onClick={() => onSubmit(messageId, resolvedReason)}
        >
          Enviar Feedback
        </FeedbackSubmitButton>
      </ModalContent>
    </ModalOverlay>
  );
}

export default FeedbackModal;

// ============ Styled Components ============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: ${props => props.theme.modal_overlay};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.modal_background || theme.base_color};
  color: ${({ theme }) => theme.font_color};
  border: 1px solid ${({ theme }) => theme.modal_border};
  border-radius: 8px;
  padding: 20px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 12px rgba(0,0,0,.1);
  position: relative;
`;

const FeedbackModalTitle = styled.div`
  color: ${({ theme }) => theme.feedback_modal_title_color};
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Noka', sans-serif;
`;

const FeedbackModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 0 16px;
`;

const FeedbackReasonButton = styled.button<{ selected: boolean }>`
  margin: 4px 0;
  font-size: 0.875rem;
  padding: 10px 14px;
  width: 100%;
  text-transform: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Sora', sans-serif;

  ${({ selected, theme }) => selected ? `
    background: ${theme.feedback_modal_button_contained_bg};
    color: ${theme.feedback_modal_button_contained_color};
    border: 1px solid ${theme.feedback_modal_button_contained_bg};

    &:hover {
      background: ${theme.feedback_modal_button_contained_hover_bg};
      border-color: ${theme.feedback_modal_button_contained_hover_bg};
    }
  ` : `
    background: ${theme.feedback_modal_button_outlined_bg};
    color: ${theme.feedback_modal_button_outlined_color};
    border: 1px solid ${theme.feedback_modal_button_outlined_border};

    &:hover {
      background: ${theme.feedback_modal_button_outlined_hover_bg};
    }
  `}
`;

const FeedbackCustomReasonLabel = styled.label`
  display: block;
  margin: 16px 0 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.feedback_modal_title_color};
  font-family: 'Sora', sans-serif;
`;

const FeedbackCustomReasonInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.feedback_modal_input_border};
  background: ${({ theme }) => theme.feedback_modal_input_bg};
  color: ${({ theme }) => theme.feedback_modal_input_color};
  font-family: 'Sora', sans-serif;
  font-size: 0.875rem;
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.feedback_modal_input_placeholder};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.feedback_modal_button_contained_bg};
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.15);
  }
`;

const FeedbackSubmitButton = styled.button<{ disabled: boolean }>`
  width: 100%;
  padding: 10px 16px;
  margin-top: 8px;
  font-size: 0.875rem;
  text-transform: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  font-family: 'Sora', sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;
  background: transparent;

  ${({ disabled, theme }) => disabled ? `
    border: 2px solid ${theme.feedback_modal_submit_button_disabled_bg};
    color: ${theme.feedback_modal_submit_button_disabled_bg};
    opacity: 0.6;
  ` : `
    border: 2px solid ${theme.feedback_modal_submit_button_bg};
    color: ${theme.feedback_modal_submit_button_bg};

    &:hover {
      background: ${theme.feedback_modal_button_outlined_hover_bg};
      border-color: ${theme.feedback_modal_submit_button_hover_bg};
      color: ${theme.feedback_modal_submit_button_hover_bg};
    }
  `}
`;

const FeedbackCloseButton = styled(IconButton)`
  padding: 6px;
  justify-content: flex-end;
  svg {
    color: ${({ theme }) => theme.feedback_modal_close_icon_color};
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: ${({ theme }) => theme.feedback_modal_close_icon_hover};
  }
`;
